// Category worlds (wave 13, pillar 9): every category opens like Discover, not as a grid. A category's world is a zone
// (zones/categories/<id>/zone.json, `category: "<id>"`) that the category's curator persona programs; this module
// gives every such zone its template:
//   hero          the curator's pick (`hero.game`) over its key art, with Play; the category's name above it
//   chips         the subcategories, each jumping to its row
//   cat:new       new in the category (listedAt)
//   cat:popular   loved on X (the only popularity signal the catalogue has; play counts are a backend request)
//   cat:all       everything in the category
//   sub:<id>      one subcategory's row
//   banner:<id>   a wide banner from the spec's `banners` (a game, a creator or another zone) between rows
// Staff picks are the spec's own `row:1`. Membership comes from the taxonomy (category/data.ts), resolved offline.

import { getCatalogue, type Catalogue, type Game } from "../../catalogue.ts";
import { lazyCover, playActions, titleArt } from "../../components.ts";
import { artUrl } from "../../catalogue.ts";
import { h, link } from "../../dom.ts";
import { trailerSrc } from "../../rows.ts";
import { DISPLAY_STYLE, TYPE_STACKS } from "../../tokens.ts";
import { posterCard } from "../../pages/home.ts";
import { registerHeroDecor, registerSection, zoneById, zoneHref, type SectionContext } from "../registry.ts";
import { ZONE_SPECS } from "../specs.ts";
import { mediaUrl, type Zone } from "../spec.ts";
import { allCategories, categoryById, type Category } from "./data.ts";

/** a category row shows at most this many, and hides under SUB_MIN (the taxonomy's floor) */
const ROW_LIMIT = 24;
const ROW_MIN = 4;
/** "New in X": listed within this many days of the category's newest game */
const NEW_DAYS = 30;

const categoryOf = (z: Zone): Category | undefined => categoryById(z.category);

// ---- hero ----

registerHeroDecor((z) => {
  const c = categoryOf(z);
  const f = c?.feature;
  if (!c || !f || f.id !== z.hero.game) return null;
  const poster = artUrl(f.art, "full") ?? undefined;
  // Play (the app) and Play in browser from the snapshot, so they paint with the hero; on a phone the browser link is
  // the one primary action (style.css), never a desktop-only download
  const about = link(`/g/${f.id}`, { class: "cat-about" }, "About the game →");
  const snapshot = { id: f.id, title: f.title, status: "released", runtime: "web", url: f.url } as Game;
  const actions = h("div", { class: "cat-hero-actions" }, playActions(snapshot, "big"), about);
  const curator = zoneById(c.curator);
  const text = h(
    "div",
    { class: "zone-hero-text cat-hero-text" },
    h("p", { class: "zone-eyebrow" }, c.group === "featured" ? "Featured" : "Category"),
    h("h1", { class: "zone-title" }, z.title),
    z.tagline ? h("p", { class: "zone-tagline" }, z.tagline) : null,
    h(
      "div",
      { class: "cat-feature" },
      h("p", { class: "cat-feature-eyebrow" }, curator ? `${curator.title} picks` : "Curator's pick"),
      titleArt({ art: f.art, title: f.title }, "cat-feature-logo", () => h("h2", { class: "cat-feature-title" }, f.title)),
      h("p", { class: "cat-feature-blurb" }, f.blurb),
      actions,
    ),
  );
  return {
    poster,
    alt: z.hero.alt || f.title,
    text,
    after: (hero, { posterOnly, reducedMotion }) => {
      // the real game once the catalogue is in: a coming-soon game swaps Play for its date; our own trailer loops
      getCatalogue()
        .then((cat) => {
          const g = cat.games.find((x) => x.id === f.id);
          if (!g || !hero.isConnected) return;
          actions.replaceChildren(...playActions(g, "big"), about);
          const src = g.trailer ? trailerSrc(g.trailer.url, location.origin) : null;
          if (!src || posterOnly || reducedMotion) return;
          const v = h("video", { class: "zone-hero-video", src, muted: true, loop: true, playsinline: true, autoplay: true, preload: "metadata", "aria-hidden": "true" });
          v.muted = true;
          v.addEventListener("playing", () => v.classList.add("on"), { once: true });
          hero.querySelector(".zone-hero-media")?.append(v);
        })
        .catch(() => {});
    },
  };
});

// ---- rows ----

function games(cat: Catalogue, ids: string[]): Game[] {
  const byId = new Map(cat.games.map((g) => [g.id, g]));
  return ids.map((id) => byId.get(id)).filter((g): g is Game => !!g && g.status !== "coming_soon");
}

/** cards a shelf shows before you scroll it: these count as seen */
const VISIBLE = 6;

/**
 * What this page has already put in front of the player (design review 19 Oct: the same game in four shelves). One
 * set per page (keyed by the page's catalogue loader), seeded with the hero, the banners and the staff picks; shelves
 * sort unseen games first (a sort, never a filter, so no row drops under its minimum) and add what they show.
 * Sections run in page order: each awaits the same catalogue promise, so their continuations queue in that order.
 */
const pageSeen = new WeakMap<object, Set<string>>();
function seenFor(ctx: SectionContext): Set<string> {
  let seen = pageSeen.get(ctx.catalogue);
  if (!seen) {
    seen = new Set([ctx.zone.hero.game ?? "", ...zoneBanners(ctx.zone.id).map((b) => b.game ?? ""), ...(ctx.zone.rows[0]?.games ?? []).slice(0, VISIBLE + 2)]);
    pageSeen.set(ctx.catalogue, seen);
  }
  return seen;
}

/**
 * A titled shelf (or, with `grid`, the whole list wrapped) of the category's games, with an anchor and a line under
 * the title; null under ROW_MIN. `ids` may need the catalogue (New sorts by listing date); null hides the row.
 */
async function catRow(ctx: SectionContext, title: string, ids: string[] | ((cat: Catalogue) => string[] | null), opts: { anchor?: string; line?: string | null; limit?: number; grid?: boolean } = {}): Promise<HTMLElement | null> {
  const cat = await ctx.catalogue();
  const want = typeof ids === "function" ? ids(cat) : ids;
  if (!want) return null;
  let list = games(cat, want).slice(0, opts.limit ?? ROW_LIMIT);
  if (list.length < ROW_MIN) return null;
  if (!opts.grid) {
    const seen = seenFor(ctx);
    list = [...list.filter((g) => !seen.has(g.id)), ...list.filter((g) => seen.has(g.id))];
    for (const g of list.slice(0, VISIBLE)) seen.add(g.id);
  }
  // the same poster cards as the zone rows (page.ts), drawn from the taxonomy's ids: a zone rule would re-filter them
  return h(
    "section",
    { class: `zone-shelf poster${opts.grid ? " cat-all" : ""}`, id: opts.anchor ?? null },
    h("h2", { class: "zone-h2" }, title),
    opts.line ? h("p", { class: "cat-line" }, opts.line) : null,
    h("ul", { class: opts.grid ? "cat-grid" : "poster-track zone-track", role: "list" }, list.map((g) => posterCard(g))),
  );
}

/**
 * The popularity row names its real source (orchestrator, 19 Oct): "Loved on X" only when every signal is X's, else
 * "Popular across the web". Play counts would make it "Most played" (a backend request).
 */
const SOURCES: Record<string, [string, string]> = { x: ["Loved on X", "What players shared most."], foundplay: ["Loved on Foundplay", "What players upvoted most."] };
export function popularTitle(source: string | null): [title: string, line: string] {
  return (source && SOURCES[source]) || ["Popular across the web", "Shared and upvoted most, wherever players found them."];
}

registerSection("cat", (ctx) => {
  const c = categoryOf(ctx.zone);
  if (!c) return null;
  switch (ctx.arg) {
    case "new":
      return catRow(
        ctx,
        `New in ${c.title}`,
        (cat) => {
          const by = new Map(cat.games.map((g) => [g.id, g.listedAt ?? 0]));
          // only what was listed in the NEW_DAYS before the category's newest game: imports land in batches, and a
          // whole batch sorted by seconds is no "new". Without dates (an older accounts) the row stays hidden.
          const newest = Math.max(0, ...c.games.map((id) => by.get(id) ?? 0));
          if (!newest) return null;
          return c.games.filter((id) => (by.get(id) ?? 0) >= newest - NEW_DAYS * 86400).sort((a, b) => (by.get(b) ?? 0) - (by.get(a) ?? 0));
        },
        { limit: 16 },
      );
    case "popular": {
      const [title, line] = popularTitle(c.popularSource);
      return catRow(ctx, title, c.popular, { line });
    }
    case "all":
      // every game, so "All N" on the chip is true (covers load lazily)
      return catRow(ctx, `Everything in ${c.title}`, c.games, { anchor: "all", limit: c.games.length, grid: true });
    default:
      return null;
  }
});

registerSection("sub", (ctx) => {
  const c = categoryOf(ctx.zone);
  const s = c?.subcategories.find((x) => x.id === ctx.arg);
  return s ? catRow(ctx, s.title, s.games, { anchor: `sub-${s.id}`, line: s.line }) : null;
});

// ---- chips ----

registerSection("chips", (ctx) => {
  const c = categoryOf(ctx.zone);
  if (!c) return null;
  // only subcategories this world places on the page, in page order: a chip never jumps to nothing
  const placed = ctx.zone.sections.filter((s) => s.startsWith("sub:")).map((s) => s.slice(4));
  const subs = placed.map((id) => c.subcategories.find((s) => s.id === id)).filter((s): s is Category["subcategories"][number] => !!s);
  if (!subs.length) return null;
  const reduce = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
  const chips: { id: string; el: HTMLAnchorElement }[] = [];
  const chip = (id: string, label: string, n: number | null, cls = "cat-chip") => {
    const el = h("a", { class: cls, href: `#${id}`, onclick: jump(id) }, label, n === null ? null : h("span", { class: "cat-chip-n" }, String(n))) as HTMLAnchorElement;
    chips.push({ id, el });
    return h("li", null, el);
  };
  function jump(id: string) {
    return (e: Event) => {
      const target = document.getElementById(id);
      if (!target) return; // the row is hidden (too few games in this catalogue): let the link do nothing harmful
      e.preventDefault();
      target.scrollIntoView({ behavior: reduce() ? "auto" : "smooth", block: "start" });
      history.replaceState(null, "", `#${id}`);
    };
  }
  // "All" leads (on a phone it's the one chip always in view); then the subcategories in page order
  const list = h("ul", { role: "list" }, chip("all", "All", c.games.length, "cat-chip all"), subs.map((s) => chip(`sub-${s.id}`, s.title, s.games.length)));
  const nav = h("nav", { class: "cat-chips", "aria-label": `${c.title} subcategories` }, list);

  // scroll-spy: the chip of the row you're in lights up and slides into view in the bar
  let current = "";
  let queued = false;
  const spy = () => {
    queued = false;
    if (!nav.isConnected) return window.removeEventListener("scroll", onScroll);
    const line = nav.getBoundingClientRect().bottom + 24;
    let on = "";
    for (const { id } of chips) {
      const t = document.getElementById(id);
      if (t && t.getBoundingClientRect().top <= line) on = id;
    }
    if (on === current) return;
    current = on;
    for (const { id, el } of chips) {
      el.classList.toggle("on", id === on);
      if (id === on) el.setAttribute("aria-current", "true");
      else el.removeAttribute("aria-current");
    }
    const el = chips.find((x) => x.id === on)?.el;
    if (el) list.scrollTo({ left: Math.max(0, el.offsetLeft - 24), behavior: reduce() ? "auto" : "smooth" });
  };
  const onScroll = () => {
    if (!queued) (queued = true), requestAnimationFrame(spy);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return nav;
});

// ---- banners ----

interface Banner {
  id: string;
  kind: "game" | "creator" | "zone";
  game?: string;
  creator?: string;
  zone?: string;
  eyebrow?: string;
  title: string;
  copy?: string;
  cta?: string;
}

/** The spec's `banners`, read from the raw spec (a category-world field: other zones and older clients ignore it). */
export function zoneBanners(zoneId: string): Banner[] {
  const raw = (ZONE_SPECS as { id?: string; banners?: unknown }[]).find((z) => z?.id === zoneId)?.banners;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (b): b is Banner =>
      !!b &&
      typeof b.id === "string" &&
      typeof b.title === "string" &&
      (b.kind === "game" || b.kind === "creator" || b.kind === "zone") &&
      (b.kind !== "game" || typeof b.game === "string") &&
      (b.kind !== "creator" || typeof b.creator === "string") &&
      (b.kind !== "zone" || typeof b.zone === "string"),
  );
}

registerSection("banner", async (ctx) => {
  const b = zoneBanners(ctx.zone.id).find((x) => x.id === ctx.arg);
  if (!b) return null;
  const cat = await ctx.catalogue();
  const g = b.game ? cat.games.find((x) => x.id === b.game) : undefined;
  let href: string;
  if (b.kind === "game") {
    if (!g) return null;
    href = `/g/${g.id}`;
  } else if (b.kind === "creator") {
    if (!cat.games.some((x) => x.creator.id === b.creator)) return null;
    href = `/c/${b.creator}`;
  } else {
    const z = zoneById(b.zone!);
    if (!z) return null;
    href = zoneHref(z);
  }
  // a game banner shows its game; a world banner its world's pick, framed by that world's focal point (review R2-1:
  // a banner without art drew an empty panel); a creator banner its named game
  let art: Record<string, unknown> = g ? lazyCover(g, "full") : {};
  if (b.kind === "zone") {
    const target = zoneById(b.zone!);
    const pick = categoryById(target?.category)?.feature;
    const src = (pick && artUrl(pick.art, "full")) ?? (target ? mediaUrl(target.hero.poster) : null);
    const [fx, fy] = target?.hero.focal ?? [0.5, 0.5];
    if (src) art = { "data-bg": src, style: { backgroundImage: `linear-gradient(160deg, ${target!.theme.palette.surface ?? target!.theme.palette.bg}, ${target!.theme.palette.bg})`, backgroundPosition: `${fx * 100}% ${fy * 100}%` } };
  }
  return h(
    "section",
    { class: `cat-banner ${b.kind}`, "aria-label": b.title },
    link(
      href,
      { class: "cat-banner-card", ...art },
      h(
        "span",
        { class: "cat-banner-text" },
        b.eyebrow ? h("span", { class: "zone-eyebrow" }, b.eyebrow) : null,
        h("strong", { class: "cat-banner-title" }, g && b.kind === "game" ? g.title : b.title),
        b.copy ? h("span", { class: "cat-banner-copy" }, b.copy) : null,
        h("span", { class: "cat-banner-cta" }, `${b.cta ?? "Open"} →`),
      ),
    ),
  );
});

// ---- the way in from Browse ----

/**
 * Every category world as a typographic tile in its own palette and display face (tiles keep real or typographic
 * covers: no key art here). Browse shows the strip above its collections, so a genre is a place, not a filter.
 */
/** custom properties (Object.assign on style can't set `--x`, so setProperty) */
function tileVars(el: HTMLElement, z: Zone): HTMLElement {
  const d = DISPLAY_STYLE[z.theme.type.display] ?? DISPLAY_STYLE.grotesk!;
  const vars: Record<string, string> = {
    "--t-bg": z.theme.palette.bg,
    "--t-ink": z.theme.palette.ink,
    "--t-accent": z.theme.palette.accent,
    "--t-font": TYPE_STACKS[z.theme.type.display] ?? TYPE_STACKS.grotesk!,
    "--t-weight": String(d.weight),
    "--t-case": d.transform,
  };
  for (const [k, v] of Object.entries(vars)) el.style.setProperty(k, v);
  return el;
}

function worldsIn(group: string | null): { c: Category; z: Zone }[] {
  return allCategories()
    .filter((c) => (c.group ?? null) === group)
    .map((c) => ({ c, z: c.zone ? zoneById(c.zone) : undefined }))
    .filter((x): x is { c: Category; z: Zone } => !!x.z);
}

/**
 * The featured worlds (a language, a controller, all ages: 25 Oct, from the Grok bot's columns) as wide tiles over
 * their hero's key art, a real screenshot. Home shows the strip under its first rows; Browse above the categories.
 */
export function featuredStrip(): HTMLElement | null {
  const worlds = worldsIn("featured");
  if (!worlds.length) return null;
  return h(
    "nav",
    { class: "cat-strip feat-strip", "aria-label": "Featured" },
    h("h2", { class: "cat-strip-h" }, "Featured"),
    h(
      "ul",
      { role: "list" },
      worlds.map(({ c, z }) => {
        const tile = tileVars(link(zoneHref(z), { class: "cat-tile feat-tile" }, h("strong", null, c.title), h("span", null, `${c.games.length} games`)), z);
        const art = mediaUrl(z.tile ?? z.hero.poster);
        if (art) tile.style.setProperty("--t-art", `url("${art}")`);
        if (z.tile) tile.classList.add("has-tile");
        return h("li", null, tile);
      }),
    ),
  );
}

export function categoryStrip(): HTMLElement | null {
  const worlds = worldsIn(null);
  if (!worlds.length) return null;
  return h(
    "nav",
    { class: "cat-strip", "aria-label": "Categories" },
    h("h2", { class: "cat-strip-h" }, "Categories"),
    h(
      "ul",
      { role: "list" },
      worlds.map(({ c, z }) => {
        const tile = tileVars(link(zoneHref(z), { class: "cat-tile" }, h("strong", null, c.title), h("span", null, `${c.games.length} games`)), z);
        const art = mediaUrl(z.tile);
        if (art) {
          tile.style.setProperty("--t-art", `url("${art}")`);
          tile.classList.add("has-tile");
        }
        return h("li", null, tile);
      }),
    ),
  );
}
